import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Control, Controller, UseFormHandleSubmit } from "react-hook-form";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { temporaryQueryConstructor } from "../AdvancedSearch/TemporaryQueryConstructor";
import { setModal } from "../../store/slice/modalSlice";
import { useLocation } from "react-router-dom";
import { ClickAwayListener } from "@mui/material";
import { useState } from "react";

interface SearchInput {
  searchterm: string;
}

interface PropType {
  fieldName: string;
  options: any;
  control: Control<any, any>;
  handleSubmit: UseFormHandleSubmit<SearchInput>;
  handleSearch: any;
  handleAutoCompleteSelect?: any;
  setValue: any;
  fullWidth?: boolean;
  loading?: boolean;
  navigate?: any;
  selectedTags?: any;
  selectedSubjects?: any;
  selectedSubjectNames?: any;
  subjectData?: any;
  suggestionsData?: any;
  wrongWord?: any;
  dropdownPlacement?: string;
  from?: string;
  /** Optional max character length applied to the input (no limit when omitted). */
  maxLength?: number;
  isSemanticMode?: boolean;
}

export default function AutoCompleteField(props: PropType) {
  const {
    fieldName,
    options,
    control,
    handleSearch,
    handleSubmit,
    handleAutoCompleteSelect,
    setValue,
    fullWidth,
    loading = false,
    navigate,
    selectedTags,
    selectedSubjects,
    selectedSubjectNames,
    subjectData,
    suggestionsData,
    wrongWord,
    dropdownPlacement,
    from,
    maxLength,
    isSemanticMode,
  } = props;

  const dispatch = useAppDispatch();
  const location = useLocation();
  const customerId = sessionStorage.getItem("informaticscustomer_id");
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const [isSpellDropdownOpen, setSpellDropdownOpen] = useState(true);
  const [spellCheck, setSpellCheck] = useState("");
  const [disableSpellCheck, setDisableSpellCheck] = useState(false);

  const l1SubjectIds = useAppSelector(
    (state) => state.searchReducer.subjectL1Type,
  );
  const uniqueIds = [...new Set(l1SubjectIds || [])];
  const encodedSubjectIds = temporaryQueryConstructor(uniqueIds);

  const can_search_mesh = useAppSelector(
    (state) => state.customer.customerDetails?.can_search_mesh,
  );

  const isAICustomer = useAppSelector(
    (state) => state.customer.customerDetails?.display_ai_in_jgate,
  );

  const subjects = useAppSelector(
    (state) => state.customer.customerDetails?.subject,
  );

  const l2Subjects: any = subjects
    ?.filter((item) => item.datsubjectmasterlevel1_id === 6)
    ?.map((item) => item.level2subject_name.trim());

  function hasCommonElement(array1: string[], array2: string[]): boolean {
    return array1?.some((item1) => array2?.includes(item1));
  }

  const hasCommonSubject: boolean = hasCommonElement(
    selectedSubjectNames,
    l2Subjects,
  );

  const handleOnInputChange = (value: string, reason: string) => {
    if (reason === "reset" && value !== "") {
      handleAutoCompleteSelect(value);
    }
  };

  function openLoginInfoModal() {
    dispatch(
      setModal({
        modalType: "MESHKEYWORD_INFO_MODEL",
        modalProps: {
          isOpen: true,
          navigate: navigate,
          selectedTags: selectedTags,
          selectedSubjects: selectedSubjects,
          selectedSubjectNames: selectedSubjectNames,
          subjectData: subjectData,
        },
      }),
    );
  }

  const displayOptions = isSemanticMode
    ? []
    : Array.isArray(options) && options?.length > 0
      ? options
      : Array.isArray(suggestionsData?.suggestions?.buckets)
        ? suggestionsData?.suggestions?.buckets
        : [];
  const isOptionsEmpty = !Array.isArray(options) || options?.length === 0;

  return (
    <>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={""}
        rules={{ required: "Try for some appropriate keywords / terms" }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            sx={{
              width: !fullWidth
                ? { xs: "100%", sm: "80%", md: "100%", lg: "100%" }
                : "100%",
              // width: "100%",
              position: "relative",
              // ✅ ADD HERE
              "& .MuiAutocomplete-inputRoot": {
                alignItems: "flex-start",
              },
            }}
            size={isXs ? "small" : "medium"}
            freeSolo={true}
            ListboxProps={{ style: { maxHeight: 150 } }}
            id="free-solo-2-demo"
            disableClearable
            onChange={(_, selectedVal, reason) => {
              handleAutoCompleteSelect(selectedVal, reason);

              // 🔹 Close spell check dropdown if user selects something
              if (reason === "selectOption") {
                setSpellDropdownOpen(false);
                setDisableSpellCheck(true); //  stop spell check after option select
              }
            }}
            onInputChange={(_, value, reason) => {
              // If spellcheck is disabled but user types new non-space character → re-enable
              if (disableSpellCheck) {
                if (reason === "input" && value.trim() !== "") {
                  setDisableSpellCheck(false);
                } else {
                  return; // stop further processing (skip spell check API + state updates)
                }
              }
              handleOnInputChange(value, reason);
              setSpellCheck(value ?? "");
              setSpellDropdownOpen(true);
              // 🔹 Only reopen dropdown when user is typing, not when selecting/resetting
              if (reason === "input") {
                setSpellDropdownOpen(true);
              }
            }}
            // options={options || []}
            options={displayOptions || []}
            inputValue={value}
            autoSelect={false}
            filterOptions={(options, state) => {
              if (state.inputValue.length < 3) return [];
              if (state.inputValue.length > 2) {
                return options.filter((item) =>
                  String(item)
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase()),
                );
              }
              return options;
            }}
            PaperComponent={({ children }) => (
              <Paper
                elevation={8}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  // right: 0,
                  width: { xs: "100%", sm: 500, md: 700, lg: 900 }, // fixed width instead of right:0
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 200px)",
                  overflowY: "auto",
                }}
              >
                {children}
              </Paper>
            )}
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  multiline={from === "airsearch"}
                  minRows={from === "airsearch" ? 2 : undefined}
                  required
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiInputBase-root": {
                      // fixed height only for non-AI screens; AI screen grows with content
                      ...(from !== "airsearch" && { height: { xs: "55px" } }),
                      ...(from === "airsearch" && {
                        minHeight: "80px",
                        alignItems: "flex-start",
                      }),
                    },
                    // 🔹 highlight wrong word
                    "& .MuiInputBase-input": {
                      textDecoration:
                        !disableSpellCheck && isOptionsEmpty && wrongWord
                          ? "underline"
                          : "none",
                      textDecorationColor:
                        !disableSpellCheck && isOptionsEmpty && wrongWord
                          ? "#e02424"
                          : undefined,
                      textUnderlineOffset: "3px",
                      textDecorationStyle:
                        !disableSpellCheck && isOptionsEmpty && wrongWord
                          ? "solid"
                          : undefined,
                      textDecorationThickness:
                        !disableSpellCheck && isOptionsEmpty && wrongWord
                          ? 2
                          : undefined,
                    },
                  }}
                  onChange={onChange}
                  onKeyUp={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      handleSubmit(handleSearch)();
                    }
                  }}
                  placeholder="Enter your Topic Keywords"
                  fullWidth
                  error={!!error}
                  inputProps={{
                    ...params.inputProps,
                    ...(typeof maxLength === "number" ? { maxLength } : {}),
                  }}
                  InputProps={{
                    ...params.InputProps,
                    // type: "search",
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        // newly added styles to move the clear button to the bottom right of the input field
                        sx={{
                          alignSelf: "flex-end", // ✅ move to bottom
                          mb: from === "airsearch" ? "10px" : "15px", // optional spacing from bottom
                        }}
                      >
                        {!loading && value ? (
                          <IconButton
                            onClick={() => setValue("searchterm", "")}
                          >
                            <CloseIcon />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                />
                {error ? (
                  <Typography
                    sx={{
                      position: "absolute",
                      color: "#e02424",
                      fontSize: { xs: 8, sm: 12 },
                    }}
                  >
                    {error.message}
                  </Typography>
                ) : null}

                {/* Character-limit message — shown when the input has reached `maxLength` */}
                {!error &&
                typeof maxLength === "number" &&
                typeof value === "string" &&
                value.length >= maxLength ? (
                  <Typography
                    sx={{
                      mt: 0.5,
                      color: "#e02424",
                      fontSize: { xs: 11, sm: 12 },
                    }}
                  >
                    Maximum {maxLength} characters allowed
                  </Typography>
                ) : null}

                {wrongWord &&
                  Array.isArray(suggestionsData?.suggestions?.buckets) &&
                  suggestionsData?.suggestions?.buckets?.length > 0 &&
                  isSpellDropdownOpen &&
                  isOptionsEmpty && (
                    <ClickAwayListener
                      onClickAway={() => setSpellDropdownOpen(false)}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top:
                            props.dropdownPlacement === "bottom"
                              ? "100%"
                              : "auto",
                          bottom:
                            props.dropdownPlacement === "top" ? "100%" : "auto",
                          left: 0,
                          // right: 0,
                          width: { xs: "100%", sm: 500, md: 700, lg: 900 }, // fixed width instead of right:0
                          maxWidth: "100%",
                          backgroundColor: "#fff",
                          border: "1px solid #e02424",
                          zIndex: 9999,
                          maxHeight: 150,
                          overflowY: "auto",
                          borderRadius: "6px",
                        }}
                      >
                        {suggestionsData?.suggestions?.buckets?.map(
                          (word: string, idx: number) => (
                            <Box
                              key={idx}
                              sx={{
                                px: 2,
                                py: 1,
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#eee" },
                              }}
                              onClick={() => setValue("searchterm", word)}
                            >
                              {word}
                            </Box>
                          ),
                        )}
                      </Box>
                    </ClickAwayListener>
                  )}

                {/* {encodedSubjectIds?.includes("6") && */}
                {can_search_mesh &&
                //  hasCommonSubject &&
                selectedTags?.includes(1) &&
                !isAICustomer &&
                (location.pathname === "/" ||
                  location.pathname === "/initialSearch") ? (
                  // &&customerId === "18"
                  <>
                    <Box sx={{ textAlign: "right", mt: 1 }}>
                      <Tooltip title="Select from MeSH" arrow>
                        <Button
                          variant="contained"
                          size={"small"}
                          onClick={openLoginInfoModal}
                          sx={{ fontSize: "12px" }}
                        >
                          MeSH Keywords
                        </Button>
                      </Tooltip>
                    </Box>
                  </>
                ) : null}
              </>
            )}
          />
        )}
      />
    </>
  );
}
